import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Lock, LogOut, Plus, Trash2, Upload, X, Save, Pencil,
  FileText, Download, ShieldAlert, FolderLock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ISO (YYYY-MM-DD) -> DD-MM-YYYY for display
const fmtDate = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
};

const CATEGORIES = [
  { key: 'firm',    label: 'Firm Docs' },
  { key: 'bharath', label: 'Bharath Docs' },
  { key: 'pradeep', label: 'Pradeep Docs' },
  { key: 'tuppad',  label: 'Tuppad Docs' },
  { key: 'ramesh',  label: 'Ramesh Docs' },
];
const catOf = (row) => (row.category || 'firm');

const FirmDocumentsPage = () => {
  const { user, ready, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('firm');

  // add-row state
  const [newDesc, setNewDesc] = useState('');
  const [newBrief, setNewBrief] = useState('');
  const [newDate, setNewDate] = useState('');
  const [adding, setAdding] = useState(false);

  // inline edit state
  const [editId, setEditId] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editBrief, setEditBrief] = useState('');
  const [editDate, setEditDate] = useState('');

  // per-row upload busy
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/firm-documents', { replace: true });
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/firm-documents`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load firm documents');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (ready && user && isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, isAdmin]);

  const addRow = async () => {
    const desc = newDesc.trim();
    if (!desc) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/admin/firm-documents`, {
        description: desc,
        brief_contents: newBrief.trim(),
        doc_date: newDate,
        category: tab,
        sort_order: (rows.length + 1) * 10,
      });
      setNewDesc(''); setNewBrief(''); setNewDate('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not add the document row');
    } finally { setAdding(false); }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditDesc(row.description || '');
    setEditBrief(row.brief_contents || '');
    setEditDate(row.doc_date || '');
  };
  const cancelEdit = () => { setEditId(null); setEditDesc(''); setEditBrief(''); setEditDate(''); };
  const saveEdit = async (row) => {
    const desc = editDesc.trim();
    if (!desc) return;
    try {
      await axios.put(`${API}/admin/firm-documents/${row.id}`, {
        description: desc,
        brief_contents: editBrief.trim(),
        doc_date: editDate,
        category: row.category || 'firm',
        sort_order: row.sort_order ?? 100,
      });
      cancelEdit();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update the row');
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Delete this document row and its ${row.files?.length || 0} file(s)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/firm-documents/${row.id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete the row');
    }
  };

  const uploadFile = async (row, file) => {
    if (!file) return;
    setUploadingId(row.id); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/firm-documents/${row.id}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
    } catch (err) {
      setError(err?.response?.data?.detail || 'File upload failed');
    } finally {
      setUploadingId(null);
      if (fileRefs.current[row.id]) fileRefs.current[row.id].value = '';
    }
  };

  const removeFile = async (row, fileId) => {
    if (!window.confirm('Remove this file?')) return;
    try {
      const { data } = await axios.delete(`${API}/admin/firm-documents/${row.id}/files/${fileId}`);
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not remove the file');
    }
  };

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="text-brand-yellow mx-auto mb-4" size={32} />
          <p className="text-white/70 text-sm">Verifying access…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4" data-testid="firm-docs-denied">
        <div className="text-center max-w-md">
          <ShieldAlert className="text-red-400 mx-auto mb-4" size={40} />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Restricted · Confidential</h1>
          <p className="text-white/60 text-sm mb-6">
            Firm Documents are available to Administrators only. If you believe you should have access,
            please contact a Super Admin.
          </p>
          <Button onClick={() => navigate('/team')} className="rounded-none bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const visibleRows = rows.filter((r) => catOf(r) === tab);
  const activeLabel = (CATEGORIES.find((c) => c.key === tab) || CATEGORIES[0]).label;

  return (
    <div className="min-h-screen bg-white" data-testid="firm-documents-page">
      {/* Admin control bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30">
        <div className="max-w-[1720px] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <FolderLock size={15} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Confidential · Admin Only
              </div>
              <div className="text-sm font-medium">Firm Documents · {rows.length} record{rows.length === 1 ? '' : 's'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/team')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              data-testid="nav-team-btn"
            >
              ← Dashboard
            </button>
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">
              {user.email}
            </span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="firm-docs-logout-btn"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#0a0f1e] pt-14 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[520px] h-[520px] bg-brand-yellow/15 blur-[150px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-4">
          <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-5">
            <Lock size={13} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
              Highly Confidential · RST &amp; Co.
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.03] tracking-tight mb-4">
            Firm Documents.
          </h1>
          <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
            A secure register of the firm&rsquo;s confidential records. Add a row describing each document,
            then attach one or more files. Visible to Administrators only.
          </p>
        </div>
      </section>

      {/* Owner tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[56px] z-20">
        <div className="max-w-[1720px] mx-auto px-4 flex items-center gap-1 overflow-x-auto py-3" data-testid="firm-docs-tabs">
          {CATEGORIES.map((c) => {
            const count = rows.filter((r) => catOf(r) === c.key).length;
            const active = tab === c.key;
            return (
              <button
                key={c.key}
                onClick={() => { setTab(c.key); cancelEdit(); }}
                className={`px-4 py-2.5 text-sm font-medium border transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-900'
                }`}
                data-testid={`firm-docs-tab-${c.key}`}
              >
                {c.label} · {count}
              </button>
            );
          })}
        </div>
      </div>

      {/* Add row */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1720px] mx-auto px-4 py-3 flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <span className="hidden lg:inline-flex items-center gap-1.5 text-[11px] font-mono tracking-wider uppercase text-gray-500 whitespace-nowrap">
            <Plus size={12} /> Adding to <span className="text-gray-900 font-semibold">{activeLabel}</span>
          </span>
          <input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (e.g. Partnership Deed, PAN of Firm)…"
            className="flex-1 border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900"
            data-testid="firm-doc-new-description"
          />
          <input
            value={newBrief}
            onChange={(e) => setNewBrief(e.target.value)}
            placeholder="Brief contents of the document…"
            className="flex-1 border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900"
            data-testid="firm-doc-new-brief"
          />
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            title="Date of the document"
            className="border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900 md:w-[160px]"
            data-testid="firm-doc-new-date"
          />
          <Button
            onClick={addRow}
            disabled={adding || !newDesc.trim()}
            className="rounded-none bg-brand-blue hover:bg-brand-blue-hover text-white px-4 h-[42px] whitespace-nowrap"
            data-testid="firm-doc-add-btn"
          >
            {adding ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Plus size={15} className="mr-1.5" />}
            Add Row
          </Button>
        </div>
      </div>

      {error && (
        <div className="max-w-[1720px] mx-auto px-4 pt-4">
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-700"><X size={15} /></button>
          </div>
        </div>
      )}

      <div className="max-w-[1720px] mx-auto px-4 py-8">
        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
        ) : visibleRows.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gray-300 bg-white text-gray-500 text-sm" data-testid="firm-docs-empty">
            No documents in <span className="font-semibold">{activeLabel}</span> yet. Add your first row above.
          </div>
        ) : (
          <div className="border border-gray-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="firm-docs-table">
              <thead className="bg-gray-900 text-white text-[10px] font-mono tracking-widest uppercase">
                <tr>
                  <th className="text-left px-3 py-3 w-14">SL No.</th>
                  <th className="text-left px-3 py-3" style={{ width: '24%' }}>Description of the Document</th>
                  <th className="text-left px-3 py-3" style={{ width: '26%' }}>Brief Contents of the Document</th>
                  <th className="text-left px-3 py-3 w-32">Date of Document</th>
                  <th className="text-left px-3 py-3">Documents</th>
                  <th className="text-right px-3 py-3 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, i) => {
                  const isEditing = editId === row.id;
                  const busy = uploadingId === row.id;
                  return (
                    <tr key={row.id} className="border-b border-gray-100 align-top hover:bg-gray-50 group" data-testid={`firm-doc-row-${row.id}`}>
                      <td className="px-3 py-4 font-mono text-[12px] tabular-nums text-gray-500">{i + 1}</td>
                      <td className="px-3 py-4">
                        {isEditing ? (
                          <textarea
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:border-gray-900"
                            data-testid={`firm-doc-edit-input-${row.id}`}
                          />
                        ) : (
                          <div className="text-gray-800 leading-relaxed whitespace-pre-wrap font-medium" data-testid={`firm-doc-desc-${row.id}`}>{row.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {isEditing ? (
                          <textarea
                            value={editBrief}
                            onChange={(e) => setEditBrief(e.target.value)}
                            rows={2}
                            placeholder="Brief contents…"
                            className="w-full border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:border-gray-900"
                            data-testid={`firm-doc-edit-brief-${row.id}`}
                          />
                        ) : (
                          <div className="text-gray-600 leading-relaxed whitespace-pre-wrap text-[13px]" data-testid={`firm-doc-brief-${row.id}`}>
                            {row.brief_contents || <span className="text-gray-300">—</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-gray-900"
                            data-testid={`firm-doc-edit-date-${row.id}`}
                          />
                        ) : (
                          <span className="font-mono text-[12px] tabular-nums text-gray-700" data-testid={`firm-doc-date-${row.id}`}>
                            {row.doc_date ? fmtDate(row.doc_date) : <span className="text-gray-300">—</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        <div className="space-y-1.5">
                          {(row.files || []).map((f) => (
                            <div key={f.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-2.5 py-1.5" data-testid={`firm-doc-file-${f.id}`}>
                              <FileText size={14} className="text-brand-blue flex-shrink-0" />
                              <a
                                href={`${API}/media/${f.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[13px] text-gray-800 hover:text-brand-blue hover:underline truncate flex-1"
                                title={`${f.filename} · ${fmtSize(f.size)}`}
                                data-testid={`firm-doc-file-link-${f.id}`}
                              >
                                {f.filename}
                              </a>
                              <span className="text-[10px] text-gray-400 tabular-nums hidden md:inline">{fmtSize(f.size)}</span>
                              <a href={`${API}/media/${f.path}`} target="_blank" rel="noopener noreferrer" title="Download / open" className="p-1 text-gray-400 hover:text-brand-blue"><Download size={13} /></a>
                              <button onClick={() => removeFile(row, f.id)} title="Remove file" className="p-1 text-gray-400 hover:text-red-600" data-testid={`firm-doc-file-remove-${f.id}`}><X size={13} /></button>
                            </div>
                          ))}
                          <input
                            ref={(el) => { fileRefs.current[row.id] = el; }}
                            type="file"
                            className="hidden"
                            onChange={(e) => uploadFile(row, e.target.files?.[0])}
                          />
                          <button
                            onClick={() => fileRefs.current[row.id]?.click()}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 text-[12px] font-medium border border-dashed border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue px-3 py-1.5 transition-colors disabled:opacity-60"
                            data-testid={`firm-doc-upload-${row.id}`}
                          >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                            {busy ? 'Uploading…' : 'Upload document'}
                          </button>
                          <p className="text-[10px] text-gray-400">PDF, Word, Excel, PPT, images · up to 50 MB each</p>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-right">
                        <div className="inline-flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(row)} title="Save" className="p-1.5 border border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue" data-testid={`firm-doc-save-${row.id}`}><Save size={13} /></button>
                              <button onClick={cancelEdit} title="Cancel" className="p-1.5 border border-gray-300 text-gray-600 hover:text-red-600" data-testid={`firm-doc-cancel-${row.id}`}><X size={13} /></button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(row)} title="Edit row" className="p-1.5 border border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue" data-testid={`firm-doc-edit-${row.id}`}><Pencil size={13} /></button>
                              <button onClick={() => removeRow(row)} title="Delete row" className="p-1.5 border border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-600" data-testid={`firm-doc-delete-${row.id}`}><Trash2 size={13} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmDocumentsPage;
